import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Grid,
    Checkbox,
    FormControlLabel,
    Button,
    IconButton,
    Paper,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Snackbar,
    Alert,
    Stack,
    Avatar,
    Chip,
    alpha,
    useTheme,
    Fade,
    Grow,
    Divider,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    WorkOutline as WorkOutlineIcon,
    LocationOnOutlined as LocationOnOutlinedIcon,
    CancelOutlined as CancelOutlinedIcon,
    CalendarTodayOutlined as CalendarTodayOutlineIcon,
    Search as SearchIcon,
    DeleteOutline as DeleteIcon,
    VisibilityOutlined as VisibilityIcon,
    Business as BusinessIcon,
    HistoryEdu as TradeIcon,
    Description as DescriptionIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';
import { createJob, getTrades, getJobs } from '../Apis/JobApis';
import { getActiveColleges } from '../Apis/CollegeApis';

const JobDrives = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [colleges, setColleges] = useState([]);
    const [trades, setTrades] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [drives, setDrives] = useState([]);
    const [tradeSearch, setTradeSearch] = useState('');
    const [collegeSearch, setCollegeSearch] = useState('');
    const [driveFilters, setDriveFilters] = useState({
        search: '',
        type: ''
    });

    const [jobData, setJobData] = useState({
        title: "",
        description: "",
        requirements: "",
        location: "",
        jobType: "full_time",
        applicationDeadline: "",
        ojtDuration: "",
        stipendAmount: "",
        isNapsNats: false,
        tradeIds: [],
        collegeIds: [],
        targetAllTrades: false,
        targetAllColleges: true
    });

    const fetchData = useCallback(async () => {
        try {
            const [collegesRes, tradesRes] = await Promise.all([
                getActiveColleges(),
                getTrades()
            ]);

            if (collegesRes?.success) setColleges(collegesRes.data || []);
            if (tradesRes?.success) setTrades(tradesRes.data || []);
        } catch (error) {
            console.error("Failed to fetch initial data:", error);
        }
    }, []);

    const fetchDrives = useCallback(async () => {
        try {
            const params = { status: 'active' };
            if (driveFilters.search) params.search = driveFilters.search;
            if (driveFilters.type) params.jobType = driveFilters.type;
            
            const drivesRes = await getJobs(params);
            if (drivesRes?.success) setDrives(drivesRes.data?.data || drivesRes.data || []);
        } catch (error) {
            console.error("Failed to fetch jobs:", error);
        }
    }, [driveFilters.search, driveFilters.type]);

    useEffect(() => {
        fetchData();
        fetchDrives();
    }, [fetchData, fetchDrives]);

    // Tab change trigger
    useEffect(() => {
        if (activeTab === 1) fetchDrives();
    }, [activeTab, fetchDrives]);

    // Backend filtering for Trades
    useEffect(() => {
        const timer = setTimeout(async () => {
            try {
                const res = await getTrades(tradeSearch);
                if (res?.success) setTrades(res.data || []);
            } catch (err) { console.error(err); }
        }, 500);
        return () => clearTimeout(timer);
    }, [tradeSearch]);

    // Backend filtering for Colleges
    useEffect(() => {
        const timer = setTimeout(async () => {
            try {
                const res = await getActiveColleges(collegeSearch);
                if (res?.success) setColleges(res.data || []);
            } catch (err) { console.error(err); }
        }, 500);
        return () => clearTimeout(timer);
    }, [collegeSearch]);

    // Backend filtering for Active Drives
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchDrives();
        }, 500);
        return () => clearTimeout(timer);
    }, [fetchDrives]);

    const handleInputChange = (field, value) => {
        setJobData(prev => ({ ...prev, [field]: value }));
    };

    const handleTradeToggle = (tradeId) => {
        setJobData(prev => {
            const tradeIds = prev.tradeIds.includes(tradeId)
                ? prev.tradeIds.filter(id => id !== tradeId)
                : [...prev.tradeIds, tradeId];
            return { ...prev, tradeIds };
        });
    };

    const handleCollegeToggle = (collegeId) => {
        setJobData(prev => {
            const collegeIds = prev.collegeIds.includes(collegeId)
                ? prev.collegeIds.filter(id => id !== collegeId)
                : [...prev.collegeIds, collegeId];
            return { ...prev, collegeIds };
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const payload = {
                title: jobData.title,
                description: jobData.description,
                requirements: jobData.requirements,
                location: jobData.location,
                jobType: jobData.jobType,
                applicationDeadline: jobData.applicationDeadline ? new Date(jobData.applicationDeadline).toISOString() : null,
                targetAllTrades: jobData.targetAllTrades,
                tradeIds: jobData.tradeIds,
                targetAllColleges: jobData.targetAllColleges,
                collegeIds: jobData.collegeIds,
                ojtDuration: jobData.jobType === 'ojt' ? jobData.ojtDuration : null,
                stipendAmount: (jobData.jobType === 'ojt' || jobData.jobType === 'apprenticeship') ? parseFloat(jobData.stipendAmount) || 0 : 0,
                isNapsNats: jobData.jobType === 'apprenticeship' ? jobData.isNapsNats : false
            };

            const res = await createJob(payload);
            if (res.success) {
                setSnackbar({ open: true, message: "Production-ready drive posted!", severity: "success" });
                setActiveTab(1);
                // Refresh list
                const drivesRes = await getJobs();
                if (drivesRes?.success) setDrives(drivesRes.data?.data || drivesRes.data || []);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Deployment failed. Check inputs.", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const [openCollegeModal, setOpenCollegeModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [selectedDrive, setSelectedDrive] = useState(null);

    const handleView = (drive) => {
        setSelectedDrive(drive);
        setOpenViewModal(true);
    };

    const scheduledDrives = drives; 

    const FormLabel = ({ children, required }) => (
        <Typography variant="body2" fontWeight="700" sx={{ mb: 1, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {children} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
        </Typography>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: 5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Opportunity Engine</Typography>
                        <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>Architect and manage industrial engagement programs.</Typography>
                    </Box>
                    <Paper elevation={0} sx={{ p: 0.5, bgcolor: '#F1F5F9', borderRadius: 4, border: '1px solid #E2E8F0', display: 'flex' }}>
                        {['Creation', 'Active'].map((label, idx) => (
                            <Button
                                key={idx}
                                onClick={() => setActiveTab(idx)}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    bgcolor: activeTab === idx ? 'white' : 'transparent',
                                    color: activeTab === idx ? '#0F172A' : '#64748B',
                                    boxShadow: activeTab === idx ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                                    '&:hover': { bgcolor: activeTab === idx ? 'white' : alpha('#64748B', 0.05) }
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </Paper>
                </Box>
            </Fade>

            {activeTab === 0 && (
                <Grow in={true} timeout={800}>
                    <Box>
                        <Grid container spacing={4}>
                            <Grid item size={{ xs: 12, lg: 8 }}>
                                <Paper sx={{ p: 4, borderRadius: 6, border: '1px solid #E2E8F0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.03)' }}>
                                    <Typography variant="h6" fontWeight="900" sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DescriptionIcon sx={{ color: theme.palette.primary.main }} /> Core Specifications
                                    </Typography>

                                    <Grid container spacing={3}>
                                        <Grid item size={{ xs: 12, md: 6 }}>
                                            <FormLabel required>Program Title</FormLabel>
                                            <TextField fullWidth placeholder="e.g. Precision Engineering Drive" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.title} onChange={(e) => handleInputChange('title', e.target.value)} />
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 6 }}>
                                            <FormLabel required>Program Type</FormLabel>
                                            <TextField select fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.jobType} onChange={(e) => handleInputChange('jobType', e.target.value)}>
                                                <MenuItem value="full_time">Placement (Full Time)</MenuItem>
                                                <MenuItem value="ojt">On-Job Training (OJT)</MenuItem>
                                                <MenuItem value="apprenticeship">Apprenticeship (NAPS/NATS)</MenuItem>
                                                <MenuItem value="internship">Campus Drive</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 6 }}>
                                            <FormLabel required>Application Deadline</FormLabel>
                                            <TextField fullWidth type="datetime-local" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.applicationDeadline} onChange={(e) => handleInputChange('applicationDeadline', e.target.value)} />
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 6 }}>
                                            <FormLabel required>Operating Location</FormLabel>
                                            <TextField fullWidth placeholder="e.g. Pune / Virtual" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.location} onChange={(e) => handleInputChange('location', e.target.value)} />
                                        </Grid>

                                        {(jobData.jobType === 'ojt' || jobData.jobType === 'apprenticeship') && (
                                            <>
                                                <Grid item size={{ xs: 12, md: 6 }}>
                                                    <FormLabel required>Monthly Allowance / Stipend</FormLabel>
                                                    <TextField fullWidth type="number" InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.stipendAmount} onChange={(e) => handleInputChange('stipendAmount', e.target.value)} />
                                                </Grid>
                                                {jobData.jobType === 'ojt' && (
                                                    <Grid item size={{ xs: 12, md: 6 }}>
                                                        <FormLabel required>Program Duration</FormLabel>
                                                        <TextField fullWidth placeholder="e.g. 15 Days" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.ojtDuration} onChange={(e) => handleInputChange('ojtDuration', e.target.value)} />
                                                    </Grid>
                                                )}
                                                {jobData.jobType === 'apprenticeship' && (
                                                    <Grid item size={{ xs: 12, md: 6 }}>
                                                        <FormLabel>Regulatory Enrollment</FormLabel>
                                                        <TextField select fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} value={jobData.isNapsNats} onChange={(e) => handleInputChange('isNapsNats', e.target.value)}>
                                                            <MenuItem value={true}>NAPS/NATS Required</MenuItem>
                                                            <MenuItem value={false}>Not Required</MenuItem>
                                                        </TextField>
                                                    </Grid>
                                                )}
                                            </>
                                        )}

                                        <Grid item size={{ xs: 12 }}>
                                            <FormLabel>Technical Prerequisites</FormLabel>
                                            <TextField multiline rows={3} fullWidth placeholder="Detail specific skills or software knowledge needed..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }} value={jobData.requirements} onChange={(e) => handleInputChange('requirements', e.target.value)} />
                                        </Grid>

                                        <Grid item size={{ xs: 12 }}>
                                            <FormLabel>Program Description</FormLabel>
                                            <TextField multiline rows={5} fullWidth placeholder="Overview of the opportunity and company culture..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }} value={jobData.description} onChange={(e) => handleInputChange('description', e.target.value)} />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            <Grid item size={{ xs: 12, lg: 4 }}>
                                <Stack spacing={4}>
                                    <Paper sx={{ p: 4, borderRadius: 6, border: '1px solid #E2E8F0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.03)' }}>
                                        <Typography variant="subtitle1" fontWeight="900" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TradeIcon sx={{ color: theme.palette.primary.main }} /> Target Cohorts (Trades)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Search Skillsets..."
                                            InputProps={{ startAdornment: <SearchIcon sx={{ color: '#94A3B8', mr: 1 }} /> }}
                                            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#F8FAFC' } }}
                                            onChange={(e) => setTradeSearch(e.target.value)}
                                        />
                                        <Box sx={{ maxHeight: 250, overflowY: 'auto', pr: 1 }}>
                                            {trades.map((trade) => (
                                                <FormControlLabel
                                                    key={trade.id}
                                                    control={<Checkbox size="small" checked={jobData.tradeIds.includes(trade.id)} onChange={() => handleTradeToggle(trade.id)} />}
                                                    label={
                                                        <Typography variant="body2" fontWeight="600" color="#475569">
                                                            {trade.name} <span style={{ color: '#94A3B8', fontSize: '0.7rem' }}>({trade.level})</span>
                                                        </Typography>
                                                    }
                                                    sx={{ width: '100%', m: 0, py: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </Paper>

                                    <Paper sx={{ p: 4, borderRadius: 6, border: '1px solid #E2E8F0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.03)' }}>
                                        <Typography variant="subtitle1" fontWeight="900" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BusinessIcon sx={{ color: theme.palette.primary.main }} /> Campus Visibility
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                            <TextField select size="small" value={jobData.targetAllColleges ? "All" : "Selected"} onChange={(e) => handleInputChange('targetAllColleges', e.target.value === "All")} sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}>
                                                <MenuItem value="All">Global (All ITIs)</MenuItem>
                                                <MenuItem value="Selected">Restricted Access</MenuItem>
                                            </TextField>
                                            {!jobData.targetAllColleges && (
                                                <IconButton onClick={() => setOpenCollegeModal(true)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, borderRadius: 2 }}><AddIcon /></IconButton>
                                            )}
                                        </Stack>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {jobData.targetAllColleges ? (
                                                <Chip label="All Active Partners" icon={<CheckIcon sx={{ fontSize: '0.8rem' }} />} sx={{ borderRadius: '8px', fontWeight: 'bold' }} color="success" variant="outlined" />
                                            ) : (
                                                jobData.collegeIds.map(cid => {
                                                    const college = colleges.find(c => c.id === cid);
                                                    return college ? (
                                                        <Chip key={cid} label={college.name} onDelete={() => handleCollegeToggle(cid)} sx={{ borderRadius: '8px', fontWeight: 'bold' }} />
                                                    ) : null;
                                                })
                                            )}
                                        </Box>
                                    </Paper>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        disabled={loading}
                                        onClick={handleSave}
                                        sx={{
                                            py: 2,
                                            borderRadius: 4,
                                            bgcolor: '#0F172A',
                                            fontWeight: '1000',
                                            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.15)',
                                            '&:hover': { bgcolor: '#1E293B' }
                                        }}
                                    >
                                        {loading ? "Initializing..." : "Publish Opportunity"}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                </Grow>
            )}

            {activeTab === 1 && (
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: '#64748B', mr: 2 }}>
                                <SearchIcon fontSize="small" />
                                <Typography variant="body2" fontWeight="800">Quick Filter</Typography>
                            </Box>
                            
                            <TextField
                                size="small"
                                placeholder="Search by Title / Location..."
                                value={driveFilters.search}
                                onChange={(e) => setDriveFilters(prev => ({ ...prev, search: e.target.value }))}
                                sx={{ minWidth: { xs: '100%', sm: 250 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />

                            <TextField
                                select
                                size="small"
                                label="Program Type"
                                value={driveFilters.type}
                                onChange={(e) => setDriveFilters(prev => ({ ...prev, type: e.target.value }))}
                                sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="full_time">Placement (Full Time)</MenuItem>
                                <MenuItem value="ojt">On-Job Training (OJT)</MenuItem>
                                <MenuItem value="apprenticeship">Apprenticeship (NAPS/NATS)</MenuItem>
                                <MenuItem value="internship">Internship</MenuItem>
                            </TextField>

                            <Button 
                                onClick={() => setDriveFilters({ search: '', type: '' })}
                                sx={{ color: '#64748B', textTransform: 'none', fontWeight: 'bold' }}
                            >
                                Reset
                            </Button>
                        </Paper>
                    </Grid>
                    {scheduledDrives.length === 0 ? (
                        <Grid item size={{ xs: 12 }}>
                            <Paper sx={{ p: 5, borderRadius: 5, border: '1px dotted #E2E8F0', textAlign: 'center' }}>
                                <Typography color="#64748B" fontWeight="600">No active opportunities match your criteria.</Typography>
                                <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={() => setDriveFilters({ search: '', type: '' })}>Clear all filters</Button>
                            </Paper>
                        </Grid>
                    ) : (
                        scheduledDrives.map((drive, idx) => (
                            <Grid item size={{ xs: 12 }} key={drive.id}>
                                <Grow in={true} timeout={400 + (idx * 100)}>
                                    <Paper sx={{
                                        p: 3,
                                        borderRadius: 5,
                                        border: '1px solid #E2E8F0',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { md: 'center' },
                                        gap: 3,
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateX(8px)', borderColor: theme.palette.primary.main }
                                    }}>
                                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                            <Avatar sx={{ width: 56, height: 56, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}><WorkOutlineIcon /></Avatar>
                                            <Box>
                                                <Typography variant="h6" fontWeight="900" color="#1E293B">{drive.title}</Typography>
                                                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} divider={<Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />}>
                                                    <Typography variant="caption" fontWeight="bold" color="#64748B" sx={{ textTransform: 'uppercase' }}>{drive.jobType}</Typography>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <CalendarTodayOutlineIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                                                        <Typography variant="caption" color="#94A3B8">{new Date(drive.applicationDeadline).toLocaleDateString()}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <LocationOnOutlinedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                                                        <Typography variant="caption" color="#94A3B8">{drive.location}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </Box>
                                        <Stack direction="row" spacing={2} sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleView(drive)}
                                                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', color: '#475569', borderColor: '#E2E8F0' }}
                                            >
                                                Overview
                                            </Button>
                                            <IconButton sx={{ color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 3 }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </Paper>
                                </Grow>
                            </Grid>
                        ))
                    )}
                </Grid>
            )}

            {/* College Selection Modal */}
            <Dialog
                open={openCollegeModal}
                onClose={() => { setOpenCollegeModal(false); setCollegeSearch(''); }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 6, p: 1 } }}
            >
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="1000">Target Campus Network</Typography>
                    <IconButton onClick={() => { setOpenCollegeModal(false); setCollegeSearch(''); }}><CloseIcon /></IconButton>
                </Box>
                <Box sx={{ px: 3, mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search Institute or Code..."
                        InputProps={{ 
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8' }} /></InputAdornment> 
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#F8FAFC' } }}
                        value={collegeSearch}
                        onChange={(e) => setCollegeSearch(e.target.value)}
                    />
                </Box>
                <TableContainer sx={{ px: 2, maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" sx={{ bgcolor: '#F8FAFC' }} />
                                <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: 'bold' }}>INSTITUTE</TableCell>
                                <TableCell sx={{ bgcolor: '#F8FAFC', fontWeight: 'bold' }}>CODE</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {colleges.map((college) => (
                                <TableRow key={college.id} hover onClick={() => handleCollegeToggle(college.id)} sx={{ cursor: 'pointer' }}>
                                    <TableCell padding="checkbox"><Checkbox checked={jobData.collegeIds.includes(college.id)} /></TableCell>
                                    <TableCell><Typography variant="body2" fontWeight="700">{college.name}</Typography></TableCell>
                                    <TableCell><Typography variant="caption" fontWeight="bold" sx={{ bgcolor: '#F1F5F9', px: 1, py: 0.2, borderRadius: 1 }}>{college.code}</Typography></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={() => { setOpenCollegeModal(false); setCollegeSearch(''); }} sx={{ fontWeight: 'bold', color: '#64748B' }}>Discard</Button>
                    <Button variant="contained" onClick={() => { setOpenCollegeModal(false); setCollegeSearch(''); }} sx={{ bgcolor: '#0F172A', borderRadius: 2, fontWeight: 'bold' }}>Lock Selection</Button>
                </Box>
            </Dialog>

            {/* View Modal */}
            <Dialog
                open={openViewModal}
                onClose={() => setOpenViewModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 6, overflow: 'hidden' } }}
            >
                <Box sx={{ bgcolor: '#0F172A', p: 4, color: 'white' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Chip label={selectedDrive?.jobType?.toUpperCase()} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 'bold' }} />
                                {selectedDrive?.company?.name && (
                                    <Chip label={selectedDrive.company.name} sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} icon={<BusinessIcon sx={{ color: 'white !important', fontSize: '1rem' }} />} />
                                )}
                            </Stack>
                            <Typography variant="h4" fontWeight="1000">{selectedDrive?.title}</Typography>
                        </Box>
                        <IconButton onClick={() => setOpenViewModal(false)} sx={{ color: 'white' }}><CancelOutlinedIcon fontSize="large" /></IconButton>
                    </Stack>
                </Box>
                <Box sx={{ p: 4 }}>
                    <Grid container spacing={4}>
                        {/* Quick Info Bar */}
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="#64748B" fontWeight="bold">LOCATION</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                            <Typography variant="body2" fontWeight="700">{selectedDrive?.location || "Not Specified"}</Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="#64748B" fontWeight="bold">DEADLINE</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                            <CalendarTodayOutlineIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                            <Typography variant="body2" fontWeight="700">{selectedDrive?.applicationDeadline ? new Date(selectedDrive.applicationDeadline).toLocaleDateString() : "No Deadline"}</Typography>
                                        </Stack>
                                    </Grid>
                                    {(selectedDrive?.jobType === 'ojt' || selectedDrive?.jobType === 'apprenticeship') && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="#64748B" fontWeight="bold">STIPEND</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Typography variant="body2" fontWeight="900" color="success.main">₹{selectedDrive?.stipendAmount || 0}</Typography>
                                                <Typography variant="caption" color="#94A3B8">/ month</Typography>
                                            </Stack>
                                        </Grid>
                                    )}
                                    {selectedDrive?.jobType === 'ojt' && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="#64748B" fontWeight="bold">DURATION</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Typography variant="body2" fontWeight="700">{selectedDrive?.ojtDuration || "N/A"}</Typography>
                                            </Stack>
                                        </Grid>
                                    )}
                                    {selectedDrive?.jobType === 'apprenticeship' && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="#64748B" fontWeight="bold">ENROLLMENT</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Chip 
                                                    label={selectedDrive?.isNapsNats ? "NAPS/NATS" : "Standard"} 
                                                    size="small" 
                                                    color={selectedDrive?.isNapsNats ? "primary" : "default"}
                                                    sx={{ fontWeight: 'bold', height: 20, fontSize: '0.65rem' }}
                                                />
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        </Grid>

                        <Grid item size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" fontWeight="900" sx={{ mb: 2, color: '#475569', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TradeIcon sx={{ fontSize: 18 }} /> TARGET TRADES
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedDrive?.targetAllTrades ? (
                                    <Chip label="All Technical Trades" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                ) : (selectedDrive?.trades && selectedDrive.trades.length > 0) ? (
                                    selectedDrive.trades.map(trade => (
                                        <Chip 
                                            key={trade.id} 
                                            label={trade.name} 
                                            variant="outlined" 
                                            size="small" 
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="#94A3B8" sx={{ fontStyle: 'italic' }}>
                                        No specific trades selected
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" fontWeight="900" sx={{ mb: 2, color: '#475569', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon sx={{ fontSize: 18 }} /> PARTICIPATING CAMPUSES
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedDrive?.targetAllColleges ? (
                                    <Chip label="All Partner ITIs" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                ) : (selectedDrive?.colleges && selectedDrive.colleges.length > 0) ? (
                                    selectedDrive.colleges.map(college => (
                                        <Chip 
                                            key={college.id} 
                                            label={college.name} 
                                            variant="outlined" 
                                            size="small" 
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="#94A3B8" sx={{ fontStyle: 'italic' }}>
                                        No campuses selected
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid item size={{ xs: 12 }}>
                            <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item size={{ xs: 12 }}>
                            <Typography variant="subtitle2" fontWeight="1000" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: '#64748B' }} /> PROGRAM DESCRIPTION
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#475569', whiteSpace: 'pre-wrap' }}>{selectedDrive?.description}</Typography>
                        </Grid>
                        <Grid item size={{ xs: 12 }}>
                            <Typography variant="subtitle2" fontWeight="1000" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckIcon sx={{ fontSize: 18, color: '#64748B' }} /> HIRING REQUIREMENTS
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#475569', whiteSpace: 'pre-wrap' }}>{selectedDrive?.requirements}</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3, fontWeight: 'bold' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default JobDrives;
